// src/pages/RotatorForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArchive, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

import { getRotatorById, saveRotator } from '../services/RotatorServices';
import { deleteImagesFolder, getImagesFolders, getProjectById, uploadGlbFile, uploadImagesFolder } from '../services/ProjectServices';
import { getTypologiesByStageId } from '../services/TypologyServices';

import './Forms.css';
import styles from './Table.module.css';

const rotatorTypes = [
  { value: 'project', label: 'Proyecto' },
  { value: 'surrooundings', label: 'Alrededores' },
  { value: 'stage', label: 'Etapa' },
  { value: 'block', label: 'Bloque' },
  { value: 'amenities', label: 'Amenidades' },
  { value: 'unit-types', label: 'Tipos de Unidad' },
];

function RotatorForm() {
  const { projectId, id } = useParams();
  const navigate = useNavigate();

  const [blocks, setBlocks] = useState([]);
  const [error, setError] = useState(null);
  const [imagesFolders, setImagesFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState({});
  const [typologies, setTypologies] = useState([]);
  const [rotator, setRotator] = useState({
    type: '',
    code: '',
    name: '',
    description: '',
    glbFile: '',
    imagesFolder: '',
    numImages: 0,
    slug: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          // Cargando datos de rotator existente
          const rotatorData = await getRotatorById(id);
          setRotator(rotatorData);

        } else {
          // Si es nuevo, no cargamos carpetas
          setImagesFolders([]);
        }
        // Solo en edición mostramos carpetas relacionadas
        const imagesFoldersData = await getImagesFolders(projectId);
        if (imagesFoldersData && imagesFoldersData.length > 0) {
          setImagesFolders(imagesFoldersData);
        } else {
          setImagesFolders([]);
        }

        const pd = await getProjectById(projectId);
        if (pd) {
          setProjectData(pd);
          const allBlocks = pd.stages.reduce((acc, stage) => {
            if (stage.blocks && stage.blocks.length > 0) {
              const stageBlocks = stage.blocks.map((block) => ({
                id: block.id,
                code: stage.stageNumber + "-" + block.code,
                name: stage.name + " " + block.name,
              }));
              return acc.concat(stageBlocks);
            }
            return acc;
          }, []);
          setBlocks(allBlocks);

          // Cargar todas las tipologías del proyecto
          const allTypologies = [];
          for (const stage of pd.stages) {
            try {
              const stageTypologies = await getTypologiesByStageId(stage.id);
              if (stageTypologies && stageTypologies.length > 0) {
                const typologiesWithStage = stageTypologies.map((typology) => ({
                  ...typology,
                  stageCode: stage.stageNumber,
                  stageName: stage.name,
                  displayName: `${stage.name} - ${typology.name}`,
                  displayCode: `${stage.stageNumber}-${typology.code}`
                }));
                allTypologies.push(...typologiesWithStage);
              }
            } catch (error) {
              console.error(`Error cargando tipologías para etapa ${stage.id}:`, error);
            }
          }
          setTypologies(allTypologies);
        }
      } catch (error) {
        setError(error.message);
        console.error(error.message);
        setImagesFolders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, projectId]);


  const getSlug = (type, code, name = null) => {
    let slugPart = code;

    if (type === 'unit-types' && name) {
      slugPart = name;
    }

    const newName = projectData.slug + "-" + type + '-' + slugPart;
    return newName.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/--+/g, '-').replace(/^-|-$/g, '');
  };

  const handleCodeChange = (e) => {
    const selectedCode = e.target.value;
    let selectedName = selectedCode;

    if (rotator.type === 'unit-types') {
      const selectedTypology = typologies.find(t => t.displayCode === selectedCode);
      selectedName = selectedTypology ? selectedTypology.name : selectedCode;
    }

    const slug = getSlug(rotator.type, selectedCode, selectedName);
    setRotator({ ...rotator, code: selectedCode, slug: slug, name: slug });
  };


  const handleDeleteImagesFolder = async (folderId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta carpeta de imágenes?')) {
      try {
        await deleteImagesFolder(folderId);
        const updatedFolders = await getImagesFolders(projectId);
        setImagesFolders(updatedFolders);
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleUnzipImagesFolder = async (folderId) => {
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        rotator.id = id;
      }
      rotator.projectId = projectId;
      rotator.projectSlug = projectData.slug;
      await saveRotator(rotator);
      navigate('/projects/edit/' + projectId);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleTypeChange = (e) => {
    const selectedType = e.target.value;
    let slugName = rotator.code;

    if (selectedType === 'unit-types' && rotator.code) {
      const selectedTypology = typologies.find(t => t.displayCode === rotator.code);
      slugName = selectedTypology ? selectedTypology.name : rotator.code;
    }

    const slug = getSlug(selectedType, rotator.code, slugName);
    setRotator({ ...rotator, type: selectedType, slug: slug, name: slug });
  };

  const handleUploadFolder = async (folderName, zipFile) => {
    setLoading(true);
    try {
      const response = await uploadImagesFolder(projectId, folderName, zipFile);
      if (response) {
        const updatedFolders = await getImagesFolders(projectId);
        setImagesFolders(updatedFolders);
      } else {
        setError('Error al subir la carpeta de imágenes');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <h2>{id ? 'Editar Orbit View' : 'Nueva Orbit View'}</h2>
        {error && (
          <div>
            <div className="error-message">Hubo un error: {error}</div>
            <br />
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="rotator-type">Tipo de Vista</label>
            <select
              id="rotator-type"
              value={rotator.type}
              onChange={handleTypeChange}
              required
            >
              <option value="">Seleccione un tipo</option>
              {rotatorTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {(rotator.type === 'stage' || rotator.type === 'block' || rotator.type === 'amenities') && (
            <div className="form-group">
              <label htmlFor="rotator-code">Código</label>
              <select
                id="rotator-type"
                value={rotator.code}
                onChange={handleCodeChange}
                required
              >
                {rotator.type === 'stage' && (
                  <>
                    <option value="">Seleccione una etapa</option>
                    {projectData.stages.map((stage) => (
                      <option key={stage.id} value={stage.code}>
                        {stage.name}
                      </option>
                    ))}
                  </>
                )}
                {(rotator.type === 'block' || rotator.type === 'amenities') && (
                  <>
                    <option value="">Seleccione un bloque</option>
                    {blocks.map((block) => (
                      <option key={block.id} value={block.code}>
                        {block.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
          )}

          {rotator.type === 'unit-types' && (
            <div className="form-group">
              <label htmlFor="rotator-code">Tipología</label>
              <select
                id="rotator-code"
                value={rotator.code}
                onChange={handleCodeChange}
                required
              >
                <option value="">Seleccione una tipología</option>
                {typologies.map((typology) => (
                  <option key={typology.id} value={typology.displayCode}>
                    {typology.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="rotator-description">Descripción</label>
            <textarea
              id="rotator-description"
              value={rotator.description}
              onChange={(e) => setRotator({ ...rotator, description: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="rotator-glbFile">Archivo GLB</label>
            <div className="file-upload-container">
              <input
                type="text"
                id="rotator-glbFile"
                value={rotator.glbFile}
                readOnly
              />
              <input
                type="file"
                id="glbFileUpload"
                accept=".glb"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = () => {
                      const base64Content = reader.result.split(',')[1];
                      const formData = { projectId: projectData.id, fileName: file.name, content: base64Content };
                      uploadGlbFile(formData)
                        .then((uploadedFilePath) => {
                          setRotator({ ...rotator, glbFile: uploadedFilePath });
                        })
                        .catch((error) => {
                          setError(error.message);
                        });
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="rotator-imagesFolder">Carpeta de Imágenes</label>
            <select
              id="rotator-imagesFolder"
              value={rotator.imagesFolder}
              onChange={(e) => setRotator({ ...rotator, imagesFolder: e.target.value })}
              required
            >
              <option value="">Seleccione una carpeta</option>
              {imagesFolders.map((folder) => (
                <option key={folder.id} value={folder.name}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="rotator-numImages">Número de Imágenes</label>
            <input id="rotator-numImages" type="number" value={rotator.numImages} readOnly />
          </div>

          <div className="form-group">
            <label htmlFor="rotator-imagesExtension">Extensión de las Imágenes</label>
            <select
              id="rotator-imagesExtension"
              value={rotator.imagesExtension}
              onChange={(e) => setRotator({ ...rotator, imagesExtension: e.target.value })}
              required
            >
              <option value="">Seleccione una extensión</option>
              {['avif', 'png', 'jpg', 'jpeg', 'jxl', 'gif', 'svg', 'webp'].map((ext) => (
                <option key={ext} value={ext}>
                  {ext.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="rotator-slug">Slug (auto-generado)</label>
            <input id="rotator-slug" type="text" value={rotator.slug} readOnly />
          </div>

          <div className="form-group">
            <button type="submit" className="btn-submit">
              {id ? 'Guardar Cambios' : 'Agregar Rotator'}
            </button>
          </div>
        </form>
        <div className="form-footer">
          <Link to={`/projects/edit/${projectId}`} className="back-link">
            Regresar a Proyecto
          </Link>
        </div>
      </div>

      <div className="stages-container">
        <div className="sections-container">
          <h2>Carpetas de imágenes</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: "50%" }}>Nombre</th>
                <th style={{ width: "15%" }}>#Img</th>
                <th style={{ width: "15%" }}>#Img Low</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {imagesFolders.map((folder) => (
                <tr key={folder.id}>
                  <td>{folder.name}</td>
                  <td>{folder.rootFiles}</td>
                  <td>{folder.lowFiles}</td>
                  <td>
                    <div className="actions-container">
                      {folder.rootFiles === 0 && folder.lowFiles === 0 && (
                        <button className='btn-delete' onClick={() => handleUnzipImagesFolder(folder.id)}>
                          <FontAwesomeIcon icon={faFileArchive} />
                        </button>
                      )}
                      <button className='btn-delete' onClick={() => handleDeleteImagesFolder(folder.id)}>
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="upload-folder-form">
            <h2>Subir Carpeta de Imágenes</h2>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const folderName = e.target.folderName.value;
                const zipFile = e.target.zipFile.files[0];
                if (!zipFile) {
                  setError('Por favor selecciona un archivo .zip');
                  return;
                }
                await handleUploadFolder(folderName, zipFile);
                e.target.reset();
              }}
            >
              <div className="form-group">
                <label htmlFor="folder-name">Nombre de la Carpeta</label>
                <input type="text" id="folder-name" name="folderName" required />
              </div>
              <div className="form-group">
                <label className='form-label' htmlFor="zip-file">Archivo .zip</label>
                <input className='form-control' type="file" id="zip-file" name="zipFile" accept=".zip" required />
              </div>
              <div className="form-group">
                <button type="submit" className="btn-submit">Subir Carpeta</button>
              </div>
            </form>
            {loading && <p style={{ marginTop: '10px' }}>Subiendo carpeta, por favor espera...</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RotatorForm;
